import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AccountControllerService } from "../../../../../core/api/controllers/account-controller/account-controller.service";
import { NotificationService } from "../../../../../core/services/notification.service";
import { ESyncType } from "../../../../../shared/enums/ESyncType";
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from "../../../../../shared/components/confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-sync-button",
  templateUrl: "./sync-button.component.html",
  styleUrls: ["./sync-button.component.scss"],
  host: {
    class: "flex w-full",
  },
})
export class SyncButtonComponent implements OnInit {
  @Input() disabled: boolean = false;
  @Input() icon: string = "sync";
  
  @Output() syncStateChanged = new EventEmitter<ESyncType>();

  currentSyncType: ESyncType = ESyncType.NoSync;
  isLoadingSync: boolean = false;

  constructor(
    private dialog: MatDialog,
    private accountController: AccountControllerService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.getSyncState();
  }

  getSyncState(): void {
    this.isLoadingSync = true;
    this.accountController.getSyncState().subscribe({
      next: (res) => {
        this.currentSyncType = res.syncType;
        this.isLoadingSync = false;
      },
      error: (error) => {
        this.notificationService.error("Failed to get sync state");
        this.isLoadingSync = false;
      }
    });
  }

  onSyncTypeChange(newSyncType: ESyncType): void {
    if (!this.disabled && !this.isLoadingSync) {
      this.openSyncTypeModal(newSyncType);
    }
  }

  openSyncTypeModal(newSyncType: ESyncType): void {
    const getSyncTypeLabel = (syncType: ESyncType): string => {
      switch (syncType) {
        case ESyncType.FullSync: return 'Full Sync';
        case ESyncType.SensitiveSync: return 'Sensitive Sync';
        case ESyncType.NoSync: return 'No Sync';
        default: return 'Unknown';
      }
    };

    const getSyncTypeDescription = (syncType: ESyncType): string => {
      switch (syncType) {
        case ESyncType.FullSync: return 'Sync all data including athletes and results';
        case ESyncType.SensitiveSync: return 'Sync everything except athletes and results';
        case ESyncType.NoSync: return 'No data will be sent to server';
        default: return '';
      }
    };
    
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `Change Sync Type to ${getSyncTypeLabel(newSyncType)}`,
          message: `You are about to change sync type from "${getSyncTypeLabel(this.currentSyncType)}" to "${getSyncTypeLabel(newSyncType)}". ${getSyncTypeDescription(newSyncType)}. Are you sure?`,
          confirmButtonLabel: "Change",
          cancelButtonLabel: "Cancel",
        } as IConfirmationDialogData,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateSyncState(newSyncType);
        }
      });
  }

  updateSyncState(newSyncType: ESyncType): void {
    this.isLoadingSync = true;
    this.accountController.updateSyncState(newSyncType).subscribe({
      next: () => {
        this.getSyncState();
        this.syncStateChanged.emit(newSyncType);
        const getSyncTypeLabel = (syncType: ESyncType): string => {
          switch (syncType) {
            case ESyncType.FullSync: return 'Full Sync';
            case ESyncType.SensitiveSync: return 'Sensitive Sync';
            case ESyncType.NoSync: return 'No Sync';
            default: return 'Unknown';
          }
        };
        this.notificationService.success(`Sync type changed to ${getSyncTypeLabel(newSyncType)}`);
      },
      error: (error) => {
        this.notificationService.error("Failed to update sync state");
        this.isLoadingSync = false;
      }
    });
  }

  get displayText(): string {
    if (this.isLoadingSync) {
      return "Loading...";
    }
    const getSyncTypeLabel = (syncType: ESyncType): string => {
      switch (syncType) {
        case ESyncType.FullSync: return 'Full Sync';
        case ESyncType.SensitiveSync: return 'Sensitive Sync';
        case ESyncType.NoSync: return 'No Sync';
        default: return 'Select Sync Type';
      }
    };
    return getSyncTypeLabel(this.currentSyncType);
  }

  get buttonColor(): string {
    return this.currentSyncType === ESyncType.NoSync ? "accent" : "warn";
  }

  protected readonly ESyncType = ESyncType;
} 